/* =============================================================================
   VIEWS / ADMIN / DAY-CLOSE — Tagesabschluss (Z-Bon) im Bondrucker-Format
   =============================================================================
   v0.76.0-beta / Session 70 / REF-5b (Modular-Split)
   ----------------------------------------------------------------------------
   Eigener Top-Level-Tab «Tagesabschluss». Zeigt:
     - Stichtags-Picker (Default heute) für den Z-Bon
     - Receipt im 80mm-Bondrucker-Look (monospace, schmaler Container)
     - Summen: Anzahl Bestellungen, Brutto, Netto, MwSt-7, MwSt-19
     - Bezahlmethoden-Splitting (Bar / Karte / Stripe / Unbezahlt)
     - Storno-/No-Show-Anzahl + Korrekturbeleg-Anzahl
     - Print-Button für den Z-Bon
   Zweite Toolbar mit Zeitraum-Buttons (Tag / Monat / Jahr / Frei) steuert
   den Zeitraum für DATEV-CSV-Export und DSFinV-K-ZIP-Export.

   S70-Refactor: 1.060 LOC zerlegt in 4 Sub-Module:
     - day-close-state.js          — _state + Date/Range/Cents-Helpers
     - day-close-render.js         — Tab-Render + Toolbars + Setters + Exports
     - day-close-zbon-action.js    — Z-Bon-Action-Modal + Archive-Persist
     - day-close-archive-detail.js — Drill-Down + Preview + Re-Send/Re-Print

   Dieses Haupt-Modul: Public-API + Re-Exports (inkl. Test-Hooks `_xxx`)
   + window.* Bindings (Backward-Compat für Inline-Handler).
============================================================================ */

import {
  _state,
  getState,
  fmtDateDE,
  parseIsoYMD,
  resolveExportRange,
  fmtRangeDE,
  centsToEur,
  _resetDayCloseStateForTests
} from './day-close-state.js';

import {
  renderDayClose,
  loadAndRenderDayClose,
  setDayCloseDate,
  setDayCloseExportMode,
  setDayCloseExportCustom,
  printDayClose,
  exportDayCloseDatev,
  exportDayCloseDsfinvk,
  openOrdersForDayClose,
  startDayCloseZBon
} from './day-close-render.js';

import {
  loadZBonArchive,
  saveZBonArchive,
  buildZBonPayload,
  buildZBonPayloadFresh,
  buildStandaloneZBonHtml,
  buildStandaloneZBonHtmlFresh,
  openZBonReceiptActionModal,
  closeZBonReceiptActionModal,
  zBonActionSend,
  zBonActionPrint,
  zBonActionPdf,
  zBonActionArchive,
  ensureZBonArchivedForDay,
  maybeAutoArchiveYesterdayZBon,
  _resetAutoArchiveGuardForTests
} from './day-close-zbon-action.js';

import {
  viewArchivedZBon,
  closeViewArchivedZBon,
  previewArchivedZBon,
  closePreviewArchivedZBon,
  resendArchivedZBonEmail,
  reprintArchivedZBon
} from './day-close-archive-detail.js';

// — Public API Re-Exports ——————————————————————————————————————

export {
  // state
  _state,
  getState,
  _resetDayCloseStateForTests,
  // render
  renderDayClose,
  loadAndRenderDayClose,
  setDayCloseDate,
  setDayCloseExportMode,
  setDayCloseExportCustom,
  printDayClose,
  exportDayCloseDatev,
  exportDayCloseDsfinvk,
  startDayCloseZBon,
  // zbon-action
  loadZBonArchive,
  saveZBonArchive,
  buildStandaloneZBonHtml,
  buildStandaloneZBonHtmlFresh,
  openZBonReceiptActionModal,
  closeZBonReceiptActionModal,
  zBonActionSend,
  zBonActionPrint,
  zBonActionPdf,
  zBonActionArchive,
  ensureZBonArchivedForDay,
  maybeAutoArchiveYesterdayZBon,
  // archive-detail
  viewArchivedZBon,
  closeViewArchivedZBon,
  previewArchivedZBon,
  closePreviewArchivedZBon,
  resendArchivedZBonEmail,
  reprintArchivedZBon
};

// — Test-Hook-Re-Exports (Pure-Function-Helpers für Test-Suite, additiv) ————
// Memory-Lesson `feedback_gg_hard_way`: lieber additive Test-Exports als
// Reflection-Tricks im Test.

export {
  parseIsoYMD as _parseIsoYMD,
  resolveExportRange as _resolveExportRange,
  centsToEur as _centsToEur,
  fmtDateDE as _fmtDateDE,
  fmtRangeDE as _fmtRangeDE,
  buildZBonPayload as _buildZBonPayload,
  buildZBonPayloadFresh as _buildZBonPayloadFresh,
  openOrdersForDayClose as _openOrdersForDayClose,
  _resetAutoArchiveGuardForTests
};

// — Backward-Compat: window-Bindings ————————————————————————————

if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderDayClose,
    loadAndRenderDayClose,
    setDayCloseDate,
    setDayCloseExportMode,
    setDayCloseExportCustom,
    printDayClose,
    exportDayCloseDatev,
    exportDayCloseDsfinvk,
    // S233 Ticket C — Z-Bon-Start mit nicht-blockierendem Unpaid-Hinweis
    startDayCloseZBon,
    // Phase 29 (S27) — Z-Bon-Beleg-Aktion-Modal + Archiv
    openZBonReceiptActionModal,
    closeZBonReceiptActionModal,
    zBonActionSend,
    zBonActionPrint,
    zBonActionPdf,
    zBonActionArchive,
    // S93-B — Auto-Archivierung Vortags-Z-Bon beim Cockpit-Start
    maybeAutoArchiveYesterdayZBon,
    loadZBonArchive,
    viewArchivedZBon,
    closeViewArchivedZBon,
    // S29: Archiv-Re-Actions (Symmetrie zum Bon-Archiv-Pattern)
    previewArchivedZBon,
    closePreviewArchivedZBon,
    resendArchivedZBonEmail,
    reprintArchivedZBon
  });
}
